import type { EntityData } from '@adornis/baseql/entities/types.js';
import { PercentageMiddleware } from '@adornis/formfield/field-middlewares/PercentageMiddleware.js';
import { DropdownSelectionInput } from '@adornis/formfield/fields/dropdown-selection-input.js';
import { NumberInput } from '@adornis/formfield/fields/number-input.js';
import { ToggleInput } from '@adornis/formfield/fields/toggle-input.js';
import type { FunctionalForm } from '@adornis/forms/use-form.js';
import { stateful } from '@adornis/functional-lit/stateful.js';
import type { ValidationFunctionOptions } from '@adornis/validation/validator.js';
import { FormGridEntry } from '@martinus/styles/client/FormGridEntry.js';
import { sgNonOptional } from '@martinus/validation/sgNonOptional.js';
import { html, nothing } from 'lit';
import type { Person } from '../../../entities/person.js';
import { WorkingStudentDegreeType } from '../../entities/employee-sections/an-working-student.js';

export const EmployeeSectionWorkingStudentForm = stateful((props: { form: FunctionalForm<EntityData<Person>> }) => {
  if (!props.form.document) return nothing;

  const workingStudent = props.form.document.mitarbeiterDaten?.anWorkingStudent?.workingStudent ?? false;

  return html`
    <div class="form-grid">
      ${FormGridEntry({
        label: 'Wollen Sie als Werkstudent beschäftigt werden?',
        render: ToggleInput({
          ...props.form.field('mitarbeiterDaten', 'anWorkingStudent', 'workingStudent'),
          label: workingStudent
            ? 'Ja, ich möchte als Werkstudent beschäftigt werden'
            : 'Nein, ich möchte regulär beschäftigt werden',
        }),
      })}
      ${
        workingStudent
          ? html`
              ${FormGridEntry({
                label: 'Welchen Abschluss streben Sie gerade an?',
                labelSubtext: 'Welche Art von Abschluss erhalten Sie am Ende Ihres aktuellen Studiengangs?',
                render: DropdownSelectionInput({
                  ...props.form.field('mitarbeiterDaten', 'anWorkingStudent', 'degreeType'),
                  selectables: Object.values(WorkingStudentDegreeType),
                  placeholder: 'Kein angestrebter Abschluss ausgewählt',
                  placeholderMode: 'hide',
                }),
              })}
              ${FormGridEntry({
                label: 'Studienanteil in Prozent?',
                labelSubtext: 'Bsp.: 100% = Vollzeit, 50% = Teilzeit',
                render: NumberInput({
                  ...PercentageMiddleware(
                    props.form.field('mitarbeiterDaten', 'anWorkingStudent', 'partTimeStudentPercentage'),
                  ),
                  placeholder: 'Kein prozentualer Studienanteil angegeben',
                  placeholderMode: 'hide',
                  suffix: '%',
                }),
              })}
              ${FormGridEntry({
                label: 'Stunden in anderen Arbeitsverträgen',
                render: NumberInput({
                  ...props.form.field('mitarbeiterDaten', 'anWorkingStudent', 'hoursInOtherContracts'),
                  placeholder: 'Keine Stunden angegeben',
                  placeholderMode: 'hide',
                }),
              })}
            `
          : nothing
      }
    </div>
  `;
});

export const EmployeeSectionWorkingStudentFormValidator = {
  workingStudent: (options: ValidationFunctionOptions<boolean, Person>) => {
    if (options.source?.mitarbeiterDaten?.anWorkingStudent?.workingStudent) {
      return sgNonOptional({ allowFalsy: true })(options);
    }
  },
  degreeType: (options: ValidationFunctionOptions<string, Person>) => {
    if (options.source?.mitarbeiterDaten?.anWorkingStudent?.workingStudent) {
      return sgNonOptional()(options);
    }
  },
  partTimeStudentPercentage: (options: ValidationFunctionOptions<number, Person>) => {
    if (options.source?.mitarbeiterDaten?.anWorkingStudent?.workingStudent) {
      return sgNonOptional({ allowFalsy: true })(options);
    }
  },
  hoursInOtherContracts: (options: ValidationFunctionOptions<number, Person>) => {
    if (options.source?.mitarbeiterDaten?.anWorkingStudent?.workingStudent) {
      return sgNonOptional({ allowFalsy: true })(options);
    }
  },
};
